import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { usersService } from "api/services/users.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input } from "components/UI/Input";
import Table, { Column } from "components/UI/Table";
import { memberDetailRouteId } from "utils/admin-user-list";
import { getApiErrorMessage } from "utils/api-error";
import { boundMemberSearchInput, decideLegacyIdentifierDeepLink, decideMemberSearch, decideTrustedPublicIdDeepLink, decideTrustedUserIdDeepLink, MEMBER_DEEP_LINK_AMBIGUOUS_NOTICE, MEMBER_DEEP_LINK_INVALID_ID_NOTICE, MEMBER_DEEP_LINK_NOT_FOUND_NOTICE, MEMBER_DEEP_LINK_PARAM_LEGACY, MEMBER_DEEP_LINK_PARAM_PUBLIC_ID, MEMBER_DETAIL_UNAVAILABLE_NOTICE, MEMBER_LIST_LEGACY_RESET_NOTICE, MEMBER_SEARCH_INVALID_NOTICE, MEMBER_SEARCH_LABEL, MEMBER_SEARCH_PHONE_NOTICE, MEMBER_SEARCH_TOO_LONG_NOTICE, MEMBER_SEARCH_TOO_SHORT_NOTICE, readLegacyUserIdState } from "utils/member-search";

import type { AdminUserListItem, AdminUserListResponse } from "api/types/user.types";
import "pages/AdminPage.scss";

// BE `AdminUserListPagination.page_size` 기본값과 맞춘다.
const PAGE_SIZE = 50;

/**
 * 딥링크 정확 일치 탐색이 훑는 **최대 페이지 수**.
 *
 * 부분일치 결과가 1페이지를 넘으면 정확히 그 아이디를 지목한 링크인데도 못 찾았다 —
 * 같은 접두를 가진 회원이 50명을 넘으면 흔히 일어난다. 그렇다고 무한히 훑으면 관리자
 * 클릭 한 번이 수백 요청이 되므로 상한을 둔다. 상한에 걸려 끊긴 탐색은 "없음" 이 아니라
 * **모른다** 이므로, 호출부가 그때는 추측으로 이동하지 않는다.
 *
 * 5페이지(기본 250건)면 흔한 접두 충돌은 덮으면서 클릭 한 번의 비용이 예측 가능하다.
 */
const MAX_LOOKUP_PAGES = 5;

type ExactScan = {
  byUserId: AdminUserListItem | null; // `user_id` 정확 일치 (legacy 해석)
  truncated: boolean; // 상한·취소로 끝까지 보지 못했다
};

/**
 * 검색 결과 **전 페이지**에서 `user_id` 정확 일치를 찾는다.
 *
 * 구 배열 응답(`isLegacyArray`)은 전량이 한 페이지이므로 첫 응답만 보면 끝이고, 신
 * envelope 은 `next` 가 없어질 때까지 이어 본다 — 양쪽 계약을 같은 함수가 처리한다.
 * 첫 페이지는 호출부가 이미 화면에 표시한 응답을 그대로 넘겨 재요청하지 않는다.
 *
 * `public_id` 는 여기서 찾지 않는다 — BE `search` 는 name/user_id 만 필터하므로 그 값이
 * 일치하는 행은 애초에 응답에 오지 않는다. 오지 않는 것을 찾는 코드는 "항상 없음" 을
 * 사실처럼 돌려준다.
 *
 * `isCancelled` 는 **다음 페이지를 요청하기 전에** 본다. 스캔은 여러 왕복에 걸치고 그
 * 사이에 사용자가 검색어를 바꾸거나 화면을 떠날 수 있다 — 끝난 의도를 위해 남은 페이지를
 * 마저 요청하지 않는다. 중간에 끊긴 결과는 "없다" 가 아니라 **모른다**(`truncated`)다.
 */
const scanExactAcrossPages = async (term: string, first: AdminUserListResponse, isCancelled: () => boolean): Promise<ExactScan> => {
  // BE 검색은 `icontains` 라 `?id=Hong` 이 `hong` 행을 받아온다 — 비교도 맞춘다.
  const lower = term.toLowerCase();
  let byUserId: AdminUserListItem | null = null;
  let data = first;
  let pageNo = 1;

  // `next` 만 믿고 따라가지 않는다 — 응답이 계약을 어겨 계속 다음 페이지를 자칭하면 클릭
  // 한 번이 요청 폭주가 된다. 응답 자체가 말하는 **실제 페이지 수**와 상한 중 작은 쪽까지만
  // 본다. 페이지 크기는 요청값이 아니라 **받은 행 수**로 센다 (BE 가 clamp 할 수 있다).
  const perPage = first.results.length > 0 ? first.results.length : PAGE_SIZE;
  const totalPages = first.count > 0 ? Math.ceil(first.count / perPage) : 1;
  const lastPage = Math.min(totalPages, MAX_LOOKUP_PAGES);

  for (;;) {
    for (const row of data.results) {
      if (!byUserId && row.user_id.toLowerCase() === lower) byUserId = row;
    }
    if (byUserId) return { byUserId, truncated: false };
    // 빈 페이지가 오면 더 볼 것이 없다 — `next` 가 남아 있어도 멈춘다.
    if (data.isLegacyArray || !data.next || data.results.length === 0) return { byUserId, truncated: false };
    // 상한에 걸려 끊긴 것만 "모른다" 다. `count` 기준으로 끝까지 본 경우는 "없다" 로 확정한다.
    if (pageNo >= lastPage) return { byUserId, truncated: pageNo >= MAX_LOOKUP_PAGES && totalPages > MAX_LOOKUP_PAGES };
    // 의도가 끝났으면 남은 페이지를 요청하지 않는다 — 본 데까지는 "모른다" 로 돌린다.
    if (isCancelled()) return { byUserId, truncated: true };

    pageNo += 1;
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await usersService.getList({ page: pageNo, page_size: PAGE_SIZE, search: term });
      data = res.data;
    } catch {
      // 중간 페이지가 실패하면 "없음" 이라고 말할 수 없다 — 여기까지 본 것만 들고 끊는다.
      // 화면은 첫 페이지를 그대로 보여주고 있으므로 표시 상태는 건드리지 않는다.
      return { byUserId, truncated: true };
    }
  }
};

// 마스킹 필드는 3상태다 — 마스킹값 / '' (연락처 없음) / '****' (마스킹 불가).
// 빈 값만 대시로 바꾸고 '****' 는 그대로 보여준다 ("없음"과 "표시 불가"는 다르다).
const maskedOrDash = (value: string | null | undefined) => (value ? value : "—");

/**
 * 상세로 갔다가 돌아왔을 때 되살릴 목록 컨텍스트.
 *
 * **URL 에 싣지 않는다.** 검색어는 개인정보이고, `?search=` 나 `#` 로 복원하면 주소창·
 * 히스토리·Referer 에 남는다 — 이 PR 이 내내 막아 온 것이다. React Router 의 navigation
 * state 는 히스토리 entry 에만 붙고 URL 에 나타나지 않는다.
 */
type ListContext = { page: number; search: string };

/** 외부에서 온 state 는 믿지 않는다 — 형식이 맞을 때만 쓴다. */
const readListContext = (state: unknown): ListContext | null => {
  const ctx = (state as { listContext?: unknown } | null)?.listContext as Partial<ListContext> | undefined;
  if (!ctx || typeof ctx !== "object") return null;
  const { page, search } = ctx;
  if (typeof page !== "number" || !Number.isInteger(page) || page < 1) return null;
  if (typeof search !== "string") return null;
  // **외부 state 를 믿지 않는다.** 조작된 history state 로 전화번호를 되살릴 수 있다 —
  // 화면이 직접 입력받았을 때와 똑같은 판정을 통과해야 쓴다. 빈 검색은 그대로 허용한다.
  if (search) {
    const decision = decideMemberSearch(search);
    if (decision.kind !== "search") return null;
  }
  return { page, search };
};

const UserList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // 입력 중인 값(`query`)과 **현재 표에 적용된** 검색어(`appliedQuery`)를 분리한다.
  // 하나로 합치면 조회를 누르지 않은 입력값이 페이지 이동 요청에 그대로 실린다.
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [results, setResults] = useState<AdminUserListItem[]>([]);
  const [count, setCount] = useState(0);
  const [next, setNext] = useState<string | null>(null);
  const [previous, setPrevious] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  // 한 번이라도 **응답을 받아** 결과를 표시했는가. `results.length === 0` 만 보면
  // 첫 응답 이전의 초기 상태를 "검색 결과가 없습니다" 로 잘못 발표한다 — 사실이 아니다.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState("");
  // 서버로 보내지 않고 로컬에서만 알리는 안내 (전화번호·최소 길이).
  const [searchNotice, setSearchNotice] = useState("");

  // 단조 증가 요청 시퀀스. 응답이 도착했을 때 **자기가 최신 요청인지** 확인하고,
  // 아니면 상태를 건드리지 않고 버린다.
  //
  // axios 인스턴스를 공유하는 구조라 AbortController 를 쓰면 signal 을 서비스 계층
  // 시그니처까지 밀어 넣어야 하고, 취소된 요청이 `CanceledError` 로 catch 에 떨어져
  // 화면에 가짜 에러가 뜬다. 화면 상태 정합성만 필요한 지점이므로 시퀀스 가드로 막는다.
  // (같은 PR 의 `NaverOrderRegister`·`ReservationListModal` 이 쓰는 `cancelled` 플래그와
  //  같은 원리다 — 저쪽은 effect 하나당 요청 하나라 플래그면 충분하고, 여기는 여러
  //  핸들러가 같은 함수를 호출해 시퀀스가 필요하다.)
  const requestSeqRef = useRef(0);

  // 현재 입력값의 최신 스냅샷. 비동기 콜백이 "지금 사용자가 무엇을 치고 있는가"를 읽어야
  // 하는데, `query` 를 effect 의존성에 넣으면 타이핑할 때마다 딥링크 effect 가 재실행돼
  // 요청이 반복된다. 렌더마다 동기화되는 ref 로 읽기만 한다.
  const queryRef = useRef("");
  queryRef.current = query;

  // 차단된 딥링크는 `?id=` 를 지우려고 `/user-list` 로 replace 이동한다. 그러면 effect 가
  // `idle` 로 한 번 더 돈다 — 그대로 두면 안내가 지워지고 전체 목록 요청까지 나간다.
  // 차단의 결과는 "요청 0 + 안내" 여야 하므로, 그 한 번의 재실행을 통째로 건너뛴다.
  const scrubbedNoticeRef = useRef("");

  // 서버 pagination — 페이지 경계·검색 필터를 모두 BE 가 소유한다.
  // 클라이언트 전량 로딩은 `page_size` 상한(200) 때문에 성립하지 않는다.
  // 반환값은 **적용된 결과**다 (낡은 응답·실패면 null). 딥링크가 정확 일치를 찾아
  // 상세로 보낼 때 필요하다 — 상태를 다시 읽으면 이 시점의 결과인지 보장할 수 없다.
  const fetchPage = useCallback(async (nextPage: number, keyword: string): Promise<AdminUserListResponse | null> => {
    const seq = ++requestSeqRef.current;
    setError("");
    // 새 요청이 시작되면 이전 안내는 낡았다 — 성공한 결과 위에 "검색할 수 없습니다" 가
    // 남아 있으면 안 된다. 요청을 **만들지 않는** 차단 분기는 이 경로를 타지 않으므로
    // 그쪽 안내는 그대로 유지된다.
    setSearchNotice("");
    setLoading(true);
    try {
      const res = await usersService.getList({
        page: nextPage,
        page_size: PAGE_SIZE,
        search: keyword,
      });
      if (seq !== requestSeqRef.current) return null; // 낡은 응답 — 버린다
      setResults(res.data.results);
      setCount(res.data.count);
      setNext(res.data.next);
      setPrevious(res.data.previous);
      // 구 bare array 응답은 전량을 한 번에 준다 — 항상 1페이지다. 그대로 두면
      // "3페이지" 라벨만 남고 이전/다음은 비활성인 모순 상태가 된다 (혼합 배포 창).
      const normalizedToFirst = !!res.data.isLegacyArray && nextPage !== 1;
      setPage(res.data.isLegacyArray ? 1 : nextPage);
      if (normalizedToFirst) setSearchNotice(MEMBER_LIST_LEGACY_RESET_NOTICE);
      setAppliedQuery(keyword);
      setHasLoaded(true);
      return res.data;
    } catch (err) {
      if (seq !== requestSeqRef.current) return null;
      // 결과·페이지 제어를 **지우지 않는다**. 페이지 제어는 `results.length > 0` 안에서만
      // 렌더되므로, 실패했다고 비우면 이전/다음 버튼과 페이지 표시가 통째로 사라져
      // 관리자가 돌아갈 길을 잃는다 (DRF 는 범위 밖 page 에 404 `"Invalid page."` 를 준다 —
      // 목록이 줄어든 뒤 새로고침하면 실제로 도달한다). 직전 화면을 그대로 두고
      // 오류만 알린 뒤, 아래 "첫 페이지" 로 명시적으로 복구하게 한다.
      // 자동 재시도는 하지 않는다 — 실패가 지속되면 무한 루프가 된다.
      setError(getApiErrorMessage(err));
      return null;
    } finally {
      // 최신 요청만 로딩을 내린다 — 낡은 응답이 진행 중인 요청의 로딩을 끄면 안 된다.
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, []);

  // 검색은 항상 1페이지부터 — 이전 페이지 번호를 유지하면 결과가 없는 페이지가 나온다.
  //
  // 전화번호 형태·2자 미만은 **요청 자체를 만들지 않는다**. 검색어는 GET 쿼리로 나가
  // access log·히스토리에 남으므로, 서버에 보내지 않는 것이 유일한 확실한 차단이다.
  // 빈 입력(idle)은 종전대로 전체 목록 조회다 — 초기 로드 경로가 여기에 얹혀 있다.
  // 차단된 검색도 **하나의 검색 의도**다. 요청을 만들지 않더라도 진행 중이던 이전 요청은
  // 무효화해야 한다 — 그러지 않으면 늦게 도착한 이전 페이지 응답이 여전히 "최신"으로
  // 인정돼 안내 문구와 표가 서로 다른 것을 가리키는 상태 모순이 남는다.
  const discardInFlight = useCallback(() => {
    requestSeqRef.current += 1;
    setLoading(false);
    setResults([]);
    setCount(0);
    setNext(null);
    setPrevious(null);
    setError("");
    // 표시 상태까지 **미제출**로 되돌린다. 이것들을 남겨두면 차단 뒤 한 글자만 더 쳐서
    // 안내가 사라지는 순간 "검색 결과가 없습니다" 가 뜬다 — 아무것도 제출하지 않았는데
    // 0건이라고 말하는 셈이고, `page`·`appliedQuery` 가 남아 다음 요청까지 오염시킨다.
    setPage(1);
    setAppliedQuery("");
    setHasLoaded(false);
  }, []);

  const runSearch = useCallback(
    (keyword: string) => {
      const decision = decideMemberSearch(keyword);
      setSubmitSeq((v) => v + 1);

      if (decision.kind === "phone-blocked" || decision.kind === "too-short" || decision.kind === "invalid-empty" || decision.kind === "too-long") {
        discardInFlight();
        // `invalid-empty` 는 **필터 없는 전체 조회로 떨어뜨리지 않는다** — 관리자가 무언가
        // 입력했는데 전체 목록이 뜨면 그것이 검색 결과처럼 보인다.
        setSearchNotice(decision.kind === "phone-blocked" ? MEMBER_SEARCH_PHONE_NOTICE : decision.kind === "invalid-empty" ? MEMBER_SEARCH_INVALID_NOTICE : decision.kind === "too-long" ? MEMBER_SEARCH_TOO_LONG_NOTICE : MEMBER_SEARCH_TOO_SHORT_NOTICE);
        return;
      }

      setSearchNotice("");
      fetchPage(1, decision.kind === "search" ? decision.term : "");
    },
    [fetchPage, discardInFlight],
  );

  const handleSearch = (keyword: string = query) => runSearch(keyword);

  // 복구 버튼을 눌러 요청이 끝나면(성공이든 실패든) 포커스를 검색 입력으로 되돌린다.
  // 버튼 자체가 사라지거나 비활성이 되는 경우가 있어, 두지 않으면 포커스가 body 로
  // 떨어져 키보드 사용자가 위치를 잃는다. 노드가 실재할 때만 만진다.
  useEffect(() => {
    if (loading) return;

    /**
     * 포커스는 **잃어버렸을 때만** 되돌린다.
     *
     * 응답이 늦게 도착하는 사이 사용자가 다른 곳(검색 입력 등)으로 옮겨 갔을 수 있다.
     * 그때 무조건 버튼으로 옮기면 **타이핑 도중에 포커스를 빼앗는다** — 실제로 이 가드가
     * 없을 때 입력 중이던 글자가 버튼으로 새어 나갔다. 포커스가 아직 body/문서 루트에
     * 있거나(=잃어버림) 방금 누른 그 버튼일 때만 개입한다.
     */
    const active = document.activeElement;
    const focusLost = !active || active === document.body || active === document.documentElement || active === prevButtonRef.current || active === nextButtonRef.current;

    const direction = pageDirectionRef.current;
    if (!focusLost) {
      pageDirectionRef.current = null;
      restoreFocusRef.current = false;
      return;
    }

    if (direction) {
      pageDirectionRef.current = null;
      const button = direction === "prev" ? prevButtonRef.current : nextButtonRef.current;
      // 경계에서 버튼이 사라지거나 비활성이면 포커스를 받을 수 없다 — body 로 떨어뜨리지
      // 않고 검색 입력으로 보낸다.
      if (button && !button.disabled) button.focus();
      else searchInputRef.current?.focus();
      return;
    }

    if (!restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    searchInputRef.current?.focus();
  }, [loading, error]);

  // 같은 문구가 다시 떠도 스크린리더가 재announce 하도록 버전을 올린다 — 텍스트가 같으면
  // DOM 변화가 없어 live region 이 조용하다. **선언은 사용 지점보다 앞에 둔다.**
  const [announceVersion, setAnnounceVersion] = useState(0);
  // 제출 자체를 센다. 같은 입력을 두 번 제출하면 안내 **문구가 같아** 상태가 바뀌지 않고,
  // 그러면 DOM 도 그대로여서 스크린리더가 두 번째 차단을 알리지 못한다 — 관리자는 조회가
  // 먹혔는지 알 길이 없다. 제출 횟수를 발표 버전에 포함해 매번 노드를 교체한다.
  const [submitSeq, setSubmitSeq] = useState(0);
  useEffect(() => {
    setAnnounceVersion((v) => v + 1);
  }, [searchNotice, error, loading, hasLoaded, count, page, results.length, submitSeq]);

  // 오류는 별도 버전이다 — 같은 오류가 연속으로 나도(재시도 → 같은 실패) 안쪽 노드가
  // 교체돼야 assertive live region 이 다시 읽는다.
  const [errorVersion, setErrorVersion] = useState(0);
  useEffect(() => {
    setErrorVersion((v) => v + 1);
  }, [error, loading]);

  /**
   * live region 이 읽는 문구 — 화면 상태를 한 문장으로 요약한 **음성 전용** 텍스트.
   *
   * 시각 텍스트는 종전 그대로 두고 이 노드가 따로 발표한다. 같은 내용이 화면과 이 노드에
   * 두 벌 존재하는 것은 **의도된 접근성 복제**다 (sr-only). 그래서 텍스트 기반 RTL 단언은
   * 시각 노드를 지목하도록 범위를 좁힌다 — 기능 단언은 그대로다.
   *
   * 우선순위: 오류(=`role="alert"` 담당이라 여기선 침묵) → 안내 → 로딩 → 건수/0건.
   * `hasLoaded` 가 없으면 첫 응답 **이전**의 빈 배열을 "검색 결과가 없습니다" 로
   * 잘못 발표한다.
   */
  const liveAnnouncement = error ? "" : searchNotice ? searchNotice : loading ? "불러오는 중" : !hasLoaded ? "" : results.length === 0 ? "검색 결과가 없습니다." : `전체 ${count}명 중 ${page}페이지, ${results.length}명 표시`;

  // 표시에 **성공한** page/검색어를 현재 `/user-list` entry 의 `listContext` 에 반영한다.
  // 그래야 여기서 상세로 들어갔다 돌아왔을 때 진입 시점이 아니라 **지금 보고 있는** 화면이
  // 복원된다. `replace` 라 히스토리에 항목을 만들지 않고 요청도 만들지 않는다.
  //
  // 딥링크(`?id=`) 처리 중과 전화번호 스크럽 중에는 건드리지 않는다 — 그 경로의 상태는
  // 아직 "표시에 성공한 화면" 이 아니고, state 를 써 넣으면 그 값이 다음 복원을 오염시킨다.
  // 복원은 **최초 진입 state** 만 본다. 이후 동기화 `replace` 로 바뀐 state 를 다시 읽으면
  // 자기 자신을 복원 소스로 삼는 셈이라 의미가 없다 — 첫 값을 고정해 둔다.
  // 복구 버튼을 누르면 그 버튼은 사라질 수 있다 — 포커스가 body 로 떨어지면 키보드
  // 사용자는 위치를 잃는다. 요청이 끝난 뒤 검색 입력으로 되돌린다.
  const searchInputRef = useRef<HTMLInputElement>(null);
  const restoreFocusRef = useRef(false);
  // 어느 페이지 버튼을 눌렀는지 — 응답 뒤 같은 버튼으로 포커스를 되돌린다. 그 버튼이
  // 경계(첫/마지막 페이지)에서 비활성이 되면 검색 입력으로 보낸다.
  const pageDirectionRef = useRef<"prev" | "next" | null>(null);
  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const initialStateRef = useRef(location.state);
  const syncedContextRef = useRef("");
  // legacy `user_id` 딥링크는 **진입 시점 state** 로만 온다. 이후 컨텍스트 동기화 `replace`
  // 가 이 entry 의 state 를 덮어쓰므로(그래서 재실행돼도 반복되지 않는다) 첫 값을 고정한다.
  const legacyUserIdState = useRef(readLegacyUserIdState(location.state)).current;

  // URL 이 실은 **식별자 조회 의도**. 사람이 검색창에 친 자유검색과 경로를 나눈다.
  //
  // 생산자가 값의 **종류를 명시**하는 계약이 먼저다 (`utils/member-search.ts` 참조):
  //  - `?public_id=<UUID>` → 목록을 거치지 않고 상세로 직접 보낸다. 요청 0건.
  //  - navigate state `legacyUserId` → 검색으로 **정확 일치**를 찾아 그 회원의 `public_id`
  //                          로만 간다. 완성형 전화번호만 막고 `0701` 같은 정상 아이디는
  //                          통과시킨다. **URL 에는 실리지 않는다.**
  //  - `?id=<...>`         → 종류 미표시 구 링크. UUID 여도 `public_id` 로 단정하지 않는다 —
  //                          두 해석이 서로 다른 회원을 가리키면 **아무 데도 가지 않는다**.
  //  - 전화번호            → 종전대로 요청하지 않는다.
  //
  // `replace: true` — 딥링크는 중간 경유지다. 히스토리에 남기면 뒤로가기가 여기로 돌아와
  // 같은 이동을 반복한다.
  // `user_id` 는 없다 — 로그인 아이디를 URL 로 받는 계약을 두지 않는다. 목록에 없는 query
  // 는 아래 정규화가 통째로 지우므로, 수기 `?user_id=0101234` 도 요청 없이 사라진다.
  const DEEP_LINK_PARAMS = [MEMBER_DEEP_LINK_PARAM_PUBLIC_ID, MEMBER_DEEP_LINK_PARAM_LEGACY];
  // 명시 계약이 실려 있으면 그것을 쓴다 — 종류 미표시 `?id=` 는 마지막 순위다.
  // **값이 빈 파라미터는 없는 것과 같다.** 값으로만 식별하지 않으면, 컨텍스트 동기화가
  // 빈 `?id=` 를 지우는 순간 파라미터 이름이 바뀐 것으로 보여 딥링크 effect 가 한 번 더
  // 돌고 전체 조회 요청이 두 번 나간다.
  const deepLinkParam = DEEP_LINK_PARAMS.find((name) => (searchParams.get(name) ?? "") !== "") ?? "";
  const deepLinkValue = deepLinkParam ? (searchParams.get(deepLinkParam) ?? "") : "";

  /**
   * state 로 온 legacy 딥링크가 **아직 처리 중**인가.
   *
   * URL 딥링크는 `deepLinkValue` 로 구분되지만 state 계약은 URL 에 흔적이 없다. 표시가
   * 없으면 아래 컨텍스트 동기화가 딥링크 처리 도중에 끼어들어 `/user-list` 로 replace 해
   * 버리고, 그것이 상세 이동을 **덮어쓴다** (같은 커밋 주기에서 나중에 실행된다).
   * 판정이 끝나 목록에 머무르기로 확정된 시점에만 내린다.
   */
  const [deepLinkPending, setDeepLinkPending] = useState(!!legacyUserIdState);

  // 값이 실제로 있는 딥링크 처리 중에만 건드리지 않는다 — 빈 값은 종전대로 idle 경로다.
  useEffect(() => {
    if (deepLinkValue || deepLinkPending) return;
    if (scrubbedNoticeRef.current) return;
    if (loading || error) return;

    const signature = `${page}\u0000${appliedQuery}`;
    if (syncedContextRef.current === signature) return;
    syncedContextRef.current = signature;

    const listContext: ListContext = { page, search: appliedQuery };
    navigate(location.pathname, { replace: true, state: { listContext } });
    // `location` 은 이 navigate 로 바뀌므로 의존성에 넣으면 루프가 된다 — 경로만 읽는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, appliedQuery, loading, error, deepLinkValue, deepLinkPending, navigate]);

  /**
   * **딥링크를 처리하기 전에 URL 을 정규화한다.**
   *
   * 식별자 파라미터만 판정해서는 통제가 끝나지 않는다 — `?id=kim&hp=010-1234-5678#010-1234-5678`
   * 처럼 형제 query·hash·중복 파라미터에 실려 온 원문은 주소창·히스토리에 남고, 이 화면에서
   * 나가는 모든 요청의 **Referer** 로 나간다. 채택한 식별자 하나만 남기고 나머지를 통째로
   * 지운 뒤에야 판정을 시작한다.
   *
   * 이미 canonical 이면 아무것도 하지 않는다 — 그러지 않으면 replace 가 자기 자신을
   * 다시 트리거해 루프가 된다.
   */
  const rawSearch = location.search;
  const rawHash = location.hash;
  const canonicalSearch = (() => {
    const params = new URLSearchParams(rawSearch);
    // 순위·빈 값 취급 모두 위 `deepLinkParam` 과 같아야 한다. 다르면 `?public_id=&id=kim`
    // 처럼 앞 파라미터가 비어 있을 때 정규화가 실제로 판정할 값을 지워 버린다.
    for (const name of DEEP_LINK_PARAMS) {
      // **첫 값**만 본다 (종전 계약) — 그중 비어 있지 않은 파라미터를 채택한다.
      const first = params.getAll(name)[0];
      if (first !== undefined && first !== "") return `?${new URLSearchParams({ [name]: first }).toString()}`;
    }
    return "";
  })();
  const needsCanonicalize = rawSearch !== canonicalSearch || rawHash !== "";

  useEffect(() => {
    if (!needsCanonicalize) return;
    navigate(`${location.pathname}${canonicalSearch}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsCanonicalize, canonicalSearch]);

  useEffect(() => {
    // 정규화가 끝나기 전에는 판정하지 않는다 — 원문이 살아 있는 상태에서 요청을 만들면
    // 그 요청의 Referer 에 원문이 실린다.
    if (needsCanonicalize) return;
    // URL 에 식별자가 없으면 **navigate state** 의 legacy 계약을 본다. 이 값은 주소창·
    // 히스토리·Referer 어디에도 나타나지 않는다 — 그래서 URL query 계약을 두지 않는다. state 가 없으면(새로고침·
    // 직접 진입) 아무 단서도 없는 것이므로 추측하지 않고 평소의 idle 경로로 간다.
    const id = deepLinkValue || legacyUserIdState;
    setQuery(id);

    // 계약별로 판정을 나눈다. 숫자 PK 로 직접 이동하는 경로는 어느 계약에도 없다 —
    // `user_id` 가 `12345678` 인 회원과 PK 12345678 이 서로 다른 사람일 수 있어 엉뚱한
    // 상세가 열리고 mutation 대상까지 바뀐다. 상세로는 **`public_id` 로만** 간다.
    // state 로 온 legacy 값은 **trusted 판정**을 쓴다 (완성형 전화번호만
    // 차단 — 앱 내부 생산자가 실재하는 아이디를 실었고, 자유 입력 접두 규칙을 적용하면
    // `0701` 같은 정상 숫자 아이디의 회원을 영영 열 수 없다).
    const decision = !deepLinkValue ? decideTrustedUserIdDeepLink(id) : deepLinkParam === MEMBER_DEEP_LINK_PARAM_PUBLIC_ID ? decideTrustedPublicIdDeepLink(id) : decideLegacyIdentifierDeepLink(id);

    // 종류가 **명시된** `?public_id=` 만 검색 없이 상세로 직행한다.
    if (decision.kind === "detail") {
      discardInFlight();
      setSearchNotice("");
      navigate(`/user-list/${decision.publicId}`, { replace: true });
      return;
    }

    /**
     * 조회하지 않고 끊는 공통 처리 — 요청 0건 + 안내 + **원문 제거**.
     *
     * 요청을 막는 것만으로는 통제가 끝나지 않는다. 차단된 값이 주소창·브라우저 히스토리에
     * 남고, 이 화면에서 나가는 후속 요청의 **Referer** 로도 실려 나간다. `replace` 라
     * 히스토리에 새 항목을 만들지 않는다. state 로만 온 값은 URL 에 애초에 없으므로
     * 지울 것이 없다 — 그때는 이동하지 않는다 (불필요한 재실행을 만들지 않는다).
     */
    // 목록에 머무르기로 확정됐다 — 컨텍스트 동기화를 다시 열어 준다.
    const settleDeepLink = () => setDeepLinkPending(false);

    const blockAndScrub = (notice: string) => {
      discardInFlight();
      setSearchNotice(notice);
      setQuery("");
      settleDeepLink();
      if (!deepLinkValue) return;
      scrubbedNoticeRef.current = notice;
      navigate("/user-list", { replace: true });
    };

    // 종류를 명시했는데 값이 그 형식이 아니다 — 잘못된 링크다. 추측해서 검색으로 흘리면
    // 그 값이 access log 에 남으므로, 요청을 만들지 않고 원문도 지운다.
    if (decision.kind === "invalid-identifier") {
      blockAndScrub(MEMBER_DEEP_LINK_INVALID_ID_NOTICE);
      return;
    }

    // 종류 미표시 `?id=<UUID>` — `public_id` 인지 `user_id` 인지 알 수 없다. 검색으로
    // 대조해 가려낼 수도 없다 (BE `search` 는 `public_id` 를 필터하지 않는다). 추측해서
    // 이동하면 절반의 확률로 **다른 회원**의 개인정보를 열고 mutation 대상까지 바뀐다.
    if (decision.kind === "ambiguous-blocked") {
      blockAndScrub(MEMBER_DEEP_LINK_AMBIGUOUS_NOTICE);
      return;
    }

    if (decision.kind === "invalid-empty") {
      discardInFlight();
      setSearchNotice(MEMBER_SEARCH_INVALID_NOTICE);
      settleDeepLink();
      return;
    }

    // `too-long` 도 **원문을 지운다**. 상한을 넘는 `?id=` 는 전화번호를 품고 있을 수 있고
    // (예: `9`x95 + `01012345678`), 안내만 세우면 그 원문이 주소창·히스토리·후속 요청의
    // Referer 로 그대로 나간다 — 차단의 이유가 전화번호든 길이든 노출 경로는 같다.
    if (decision.kind === "phone-blocked" || decision.kind === "too-long") {
      blockAndScrub(decision.kind === "too-long" ? MEMBER_SEARCH_TOO_LONG_NOTICE : MEMBER_SEARCH_PHONE_NOTICE);
      return;
    }

    if (decision.kind === "idle") {
      // 방금 차단 때문에 URL 을 지운 재실행이면 안내를 유지하고 요청을 만들지 않는다.
      if (scrubbedNoticeRef.current) {
        setSearchNotice(scrubbedNoticeRef.current);
        scrubbedNoticeRef.current = "";
        return;
      }
      setSearchNotice("");
      // 상세에서 돌아온 entry 면 저장된 페이지·검색어를 그대로 되살린다.
      // state 가 없거나 형식이 틀리면 1페이지·빈 검색 (직접 진입·딥링크 포함).
      const restored = readListContext(initialStateRef.current);
      const restoredSearch = restored?.search ?? "";
      // 입력창과 **적용 검색어**를 함께 맞춘다. `appliedQuery` 가 비어 있으면 복원한 페이지
      // 요청이 실패했을 때 `첫 페이지`·`이전/다음` 이 필터 없는 요청을 보낸다.
      setQuery(restoredSearch);
      setAppliedQuery(restoredSearch);
      fetchPage(restored?.page ?? 1, restoredSearch);
      return;
    }

    setSearchNotice("");
    let cancelled = false;
    // 상세로 떠났는가 — 떠났으면 컨텍스트 동기화를 다시 열지 않는다 (열면 그 replace 가
    // 방금 만든 상세 이동을 덮어쓴다).
    let navigated = false;
    const goDetail = (routeId: string) => {
      navigated = true;
      navigate(`/user-list/${routeId}`, { replace: true });
    };
    // 여기까지 온 것은 `lookup` 하나뿐이다 — 종류가 **명시된** legacy `user_id` 다.
    const term = decision.term;
    fetchPage(1, term)
      .then(async (first) => {
        if (cancelled || first === null) return;
        // 응답이 오는 동안 사용자가 검색어를 고쳐 쳤다면 **의도가 바뀐 것**이다.
        // 시퀀스 가드는 "새 요청이 생겼는가"만 보는데, 입력만 바꾸고 아직 조회를 누르지 않은
        // 상태에서는 새 요청이 없어 가드가 서지 않는다 (조회 버튼은 로딩 중 비활성이라
        // 이 창에서 의도를 바꾸는 경로는 입력뿐이다). URL 도 그대로라 cleanup 의 `cancelled`
        // 역시 서지 않는다. 그 사이 어디에도 의도 변화를 보는 지점이 없어서, 늦게 도착한
        // 응답이 사용자가 치던 화면을 빼앗고 상세로 끌고 갔다.
        //
        // 이동만 조건부로 만든다 — 목록 갱신·시퀀스 가드·차단 판정은 그대로 둔다.
        // 요청은 이미 나갔고 결과도 받았으니 표시할 이유는 그대로 있다.
        // `decision.term` 은 이미 trim 된 값이다. 입력 원문과 그대로 비교하면
        // `?id=alice%20` 처럼 URL 에 공백·개행이 붙은 것만으로 "의도가 바뀌었다" 로 보고
        // 상세 이동을 건너뛴다 — 사용자는 아무것도 건드리지 않았는데.
        if (queryRef.current.trim() !== term) return;

        // 정확 일치는 **1페이지에 갇히지 않는다**. 같은 접두를 가진 회원이 페이지 크기를
        // 넘으면 정확히 그 아이디를 지목한 링크인데도 못 찾았다. 구 배열 응답은 한 페이지가
        // 전량이라 첫 응답으로 끝난다.
        // 스캔이 다음 페이지를 요청하기 **전에** 의도가 아직 살아 있는지 본다 — 끝난 의도를
        // 위해 최대 4건의 요청을 더 만들지 않는다.
        const scan = await scanExactAcrossPages(term, first, () => cancelled || queryRef.current.trim() !== term);
        if (cancelled) return;
        // 스캔이 여러 요청에 걸치는 동안에도 사용자는 입력을 바꿀 수 있다 — 다시 본다.
        if (queryRef.current.trim() !== term) return;

        const legacyRouteId = scan.byUserId ? memberDetailRouteId(scan.byUserId) : "";
        // 정확 일치를 찾았는데 그 회원에게 `public_id` 가 없으면 상세를 열 수 없다 —
        // "못 찾았다" 와는 다른 사실이므로 그렇게 알린다.
        if (scan.byUserId && !legacyRouteId) {
          setSearchNotice(MEMBER_DETAIL_UNAVAILABLE_NOTICE);
          return;
        }

        if (legacyRouteId) {
          goDetail(legacyRouteId);
          return;
        }
        // 결과가 하나도 없으면 "아래 부분일치 결과에서 확인하세요" 는 거짓이다 — 아래에
        // 아무것도 없다. 그때는 평소의 "검색 결과가 없습니다." 만 보여준다.
        // 스캔이 끊겼으면(상한·취소·중간 실패) "없다" 가 아니라 **모른다** 이므로, 조용히
        // 넘어가지 않고 못 찾았다는 사실을 알린다 — 추측으로 이동하는 경로는 없다.
        if (first.results.length > 0 || scan.truncated) setSearchNotice(MEMBER_DEEP_LINK_NOT_FOUND_NOTICE);
      })
      .finally(() => {
        // 이동하지 않았다면 이 화면에 남는다 — 그때만 동기화를 다시 연다.
        if (!navigated) settleDeepLink();
      });
    return () => {
      cancelled = true;
    };
    // `searchParams` **객체** 에 의존하면 컨텍스트 동기화의 `replace` 로도 재실행돼
    // 요청이 한 번 더 나간다 — 실제로 바뀌는 것은 식별자 이름·값뿐이므로 문자열에 의존한다.
  }, [deepLinkParam, deepLinkValue, legacyUserIdState, needsCanonicalize, fetchPage, discardInFlight, navigate]);

  /**
   * 상세로 보낸다 — 마우스 행 클릭과 키보드 셀 버튼이 **같은 경로**를 쓴다.
   *
   * 상세는 `public_id` 로만 연다. 없으면 이동하지 않고 사실을 알린다 — `user_id` 로
   * `/user-list/{id}` 를 만들면 존재하지 않는 경로가 된다.
   */
  const openMemberDetail = (row: AdminUserListItem) => {
    const routeId = memberDetailRouteId(row);
    if (!routeId) {
      setSearchNotice(MEMBER_DETAIL_UNAVAILABLE_NOTICE);
      return;
    }
    setSearchNotice("");
    // 현재 목록 entry 를 컨텍스트와 함께 다시 쓴다 — 브라우저 뒤로가기가 돌아올 자리가
    // 바로 이 entry 다. 상세 entry 에도 같은 값을 실어 `목록으로`(navigate(-1)) 와
    // 뒤로가기가 **둘 다** 복원되게 한다. 현재 entry 의 컨텍스트는 동기화 effect 가 이미
    // 맞춰 뒀다 — 여기서는 상세 entry 에만 실어 준다.
    const listContext: ListContext = { page, search: appliedQuery };
    navigate(`/user-list/${routeId}`, { state: { listContext } });
  };

  const roleVariant = (user: AdminUserListItem): "rejected" | "info" | "default" => (user.is_admin ? "rejected" : user.is_staff ? "info" : "default");

  const roleLabel = (user: AdminUserListItem) => (user.is_admin ? "관리자" : user.is_staff ? "강사" : "수강생");

  const columns: Column<AdminUserListItem>[] = [
    {
      key: "public_id",
      title: "프로필",
      width: "60px",
      // 목록 응답에는 프로필 이미지가 없다 (최소 경계) — 이니셜 아바타로 대체한다.
      render: (_, row) => (
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "#6366F1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 14,
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {row.name?.[0] ?? "?"}
        </div>
      ),
    },
    { key: "name", title: "이름", width: "120px" },
    {
      key: "user_id",
      title: "아이디",
      width: "140px",
      // **회원 식별 셀 = 상세로 가는 키보드 동선.** 행(`<tr>`)을 버튼으로 만들면 행 안의
      // 인터랙티브 요소를 가리고 표의 row 의미도 깨진다 — 대표 셀에 진짜 `<button>` 을 둔다.
      // 접근 이름에 회원을 식별할 값을 넣는다: 목록의 버튼은 이름만으로는 서로 구분되지
      // 않아 스크린리더 사용자가 "버튼" 이 몇 개 나열됐는지만 듣게 된다.
      render: (v, row) => (
        <button
          type="button"
          className="member-id-link"
          aria-label={`${row.name ?? ""} ${String(v)} 회원 상세 보기`.trim()}
          onClick={(e) => {
            // 행 클릭(마우스 편의)까지 함께 발화하면 같은 이동이 두 번 일어난다.
            e.stopPropagation();
            openMemberDetail(row);
          }}
        >
          {String(v)}
        </button>
      ),
    },
    { key: "user_email_masked", title: "이메일", render: (v) => maskedOrDash(v as string) },
    { key: "hp_masked", title: "휴대폰", width: "130px", render: (v) => maskedOrDash(v as string) },
    {
      key: "is_admin",
      title: "역할",
      width: "90px",
      render: (_, row) => <Badge variant={roleVariant(row)}>{roleLabel(row)}</Badge>,
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader title="회원 목록" description="회원명 또는 아이디로 회원 정보를 조회합니다." />

      <div className="admin-page__card">
        {/* **항상 마운트되는** live region. 조건부로 붙였다 떼면 스크린리더가 변화를
            알아채지 못한다. 오류는 아래 `role="alert"` 가 따로 알린다. */}
        <div
          data-sr-live
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
          }}
        >
          {/* 같은 문구가 다시 떠도 키가 바뀌어 노드가 교체되므로 재announce 된다. */}
          <span key={announceVersion}>{liveAnnouncement}</span>
        </div>
        {/* 오류 전용 assertive live region — **항상 마운트**한다. 조건부로 나타나면
            스크린리더가 "새로 생긴 영역" 으로 보고 지나친다. 안쪽 노드는 버전 키로
            교체돼, 재시도가 **같은 오류**로 끝나도 다시 읽힌다. */}
        <div
          data-sr-live
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          className="sr-only"
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
          }}
        >
          <span key={errorVersion}>{error}</span>
        </div>
        <div className="admin-page__filter">
          <Input
            // placeholder 는 접근 이름으로 신뢰할 수 없다 — 번역·확장에 따라 사라지고,
            // 값이 들어가면 스크린리더가 더 이상 읽지 않는다. 공용 라벨을 직접 건다.
            aria-label={MEMBER_SEARCH_LABEL}
            placeholder="회원명/아이디로 검색"
            value={query}
            onChange={(e) => {
              // HTML `maxLength` 대신 여기서 자른다 — 속성은 붙여넣기를 UTF-16 코드 단위로
              // 잘라 astral 문자를 반으로 쪼갠다. state 에는 상한+1 까지만 들어가므로
              // 이후 판정·렌더가 붙여넣기 길이에 좌우되지 않는다.
              setQuery(boundMemberSearchInput(e.target.value));
              // 입력만 바꾼 것으로 오류를 지우지 않는다 — `첫 페이지` 복구 버튼까지 사라져
              // 빈 결과처럼 보인다. 오류는 실제 요청이 시작될 때(`fetchPage`)만 지운다.
              setSearchNotice("");
            }}
            onKeyDown={(e) => e.key === "Enter" && !loading && handleSearch()}
            style={{ width: 220 }}
            ref={searchInputRef}
            prefix="🔍"
          />
          <Button variant="secondary" size="sm" disabled={loading} onClick={() => handleSearch()}>
            조회
          </Button>
          {error && (
            <>
              {/* 결과가 남아 있을 때만 상단에 알린다 — 결과가 0이면 본문 alert 하나로 끝낸다
                  (같은 오류를 두 곳에서 읽으면 중복 announce 가 된다). */}
              {/* 시각 문구에는 역할을 주지 않는다 — 발표는 위 assertive live region 하나가
                  맡는다. 두 곳에 role="alert" 를 두면 같은 오류를 두 번 읽는다. */}
              {results.length > 0 && <span style={{ fontSize: 13, color: "#EF4444" }}>{error}</span>}
              {/* 자동 재시도 대신 명시적 복구 — 범위 밖 page 에 갇혀도 한 번에 돌아온다. */}
              {/* `appliedQuery` 를 그대로 다시 쏘지 않는다 — 그 사이 사용자가 입력을
                  전화번호로 바꿨다면 privacy gate 를 우회해 나간다. 조회 버튼과 **같은
                  판정**을 거쳐 page 1 을 요청한다. */}
              <Button
                variant="secondary"
                size="sm"
                disabled={loading}
                onClick={() => {
                  restoreFocusRef.current = true;
                  runSearch(query);
                }}
              >
                첫 페이지
              </Button>
            </>
          )}
          {searchNotice && <span style={{ fontSize: 13, color: "#71717A" }}>{searchNotice}</span>}
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#71717A" }}>불러오는 중...</div>
        ) : error && results.length === 0 ? (
          // 보여줄 결과가 없는 실패(초기 로드·검색 첫 요청)를 "검색 결과가 없습니다" 로
          // 표시하면 관리자가 **서버 장애를 조건에 맞는 회원 없음**으로 오해한다.
          // 오류를 empty 보다 먼저, 스크린리더에도 알린다. 복구는 위 `첫 페이지` 버튼.
          <div style={{ padding: 40, textAlign: "center", color: "#EF4444" }}>목록을 불러오지 못했습니다 — {error}</div>
        ) : results.length === 0 ? (
          // 차단·무효로 안내가 떠 있으면 "결과 없음" 을 **병기하지 않는다** — 관리자가
          // "그런 회원이 없다" 로 읽는다. 이유는 위 안내가 이미 말하고 있다.
          // 결과가 있는 경우(예: legacy 정규화 안내)는 표를 그대로 보여준다.
          // `hasLoaded` 가 없으면 **아직 아무것도 조회하지 않은** 상태다 (초기 진입,
          // 차단 뒤 미제출). 그때 "검색 결과가 없습니다" 를 띄우면 거짓이다.
          searchNotice || !hasLoaded ? null : (
            /* 역할은 위 통합 status live region 이 갖는다 — 여기에 또 주면 status 가 둘이다. */
            <div style={{ padding: 40, textAlign: "center", color: "#71717A" }}>검색 결과가 없습니다.</div>
          )
        ) : (
          <>
            <Table
              columns={columns}
              data={results}
              rowKey="user_id"
              // 마우스 편의 경로. 키보드·스크린리더 동선은 아이디 셀의 실제 버튼이 맡는다.
              onRowClick={openMemberDetail}
            />

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 4px",
              }}
            >
              <span style={{ fontSize: 13, color: "#71717A" }}>
                전체 {count}명 · {page}페이지
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  ref={prevButtonRef}
                  variant="secondary"
                  size="sm"
                  disabled={!previous || loading}
                  onClick={() => {
                    pageDirectionRef.current = "prev";
                    fetchPage(page - 1, appliedQuery);
                  }}
                >
                  이전
                </Button>
                <Button
                  ref={nextButtonRef}
                  variant="secondary"
                  size="sm"
                  disabled={!next || loading}
                  onClick={() => {
                    pageDirectionRef.current = "next";
                    fetchPage(page + 1, appliedQuery);
                  }}
                >
                  다음
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserList;
